// Agent API surface: extraction runs, the operator conversation, and the proposal review queue.
// Built on the shared request helper in api/client.ts — never raw fetch in components.
import { request } from './client.js';

// ───────────────────────── health & workspace ─────────────────────────

export interface AgentHealth {
  vllm: { reachable: boolean; base_url: string; model_id: string; models: string[]; error: string | null };
  opencode: { available: boolean; binary: string; version: string | null; error: string | null };
  runtime: { active_runs: number; max_concurrent: number; max_wall_clock_ms: number; max_turns: number };
}

export interface WorkspaceState {
  engagement_id: number;
  code: string;
  dir: string;
  exists: boolean;
  commit: string | null;
  committed_at: string | null;
  document_files: number;
  has_agents_md: boolean;
  has_tools: boolean;
  has_skills: boolean;
}

// ───────────────────────── runs ─────────────────────────

export type RunMode = 'batch' | 'targeted' | 'conversation';

export type PassKind = 'deliverables' | 'acceptance_criteria' | 'test_cases';

export interface PassOption {
  kind: PassKind;
  label: string;
  /** How many rows the pass would work on. */
  covers: number;
  unit: 'requirement' | 'criterion';
  blurb: string;
  ready: boolean;
  blocked_reason: string | null;
}

export interface PassPlan {
  engagement_id: number;
  passes: PassOption[];
  /** Requirements still in draft — a pass over unconfirmed scope is usually premature. */
  still_draft: number;
}
export type RunStatus = 'queued' | 'starting' | 'running' | 'done' | 'error' | 'aborted' | 'timeout';

export interface RunStats {
  turns?: number;
  tool_calls?: number;
  tool_errors?: number;
  tool_rejections?: number;
  tool_lookup_misses?: number;
  proposals?: number;
  open_questions?: number;
  tokens_in?: number;
  tokens_out?: number;
  tokens_reasoning?: number;
}

export interface RunRow {
  id: number;
  engagement_id: number;
  mode: RunMode;
  status: RunStatus;
  prompt: string | null;
  opencode_session_id: string | null;
  opencode_port: number | null;
  model_id: string | null;
  config_hash: string | null;
  skill_commit: string | null;
  workspace_commit: string | null;
  /** The deliverable a workbench conversation was launched from (DEL-03); null elsewhere. */
  context_ref: string | null;
  docs_in_scope: number[];
  docs_covered: number[];
  stats: RunStats;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  proposal_count: number;
  pending_count: number;
  live?: boolean;
}

export interface TranscriptPart {
  type: 'text' | 'reasoning' | 'tool' | string;
  text?: string;
  tool?: string;
  status?: string | null;
  title?: string | null;
  input?: unknown;
  output?: string;
  error?: string | null;
}

export interface TranscriptMessage {
  id: string | null;
  role: string;
  created: number | null;
  model: string | null;
  parts: TranscriptPart[];
}

/** One tool call of the turn in flight, as the activity feed shows it. */
export interface TurnToolActivity { tool: string; detail: string; status: string }
/** What the agent is doing RIGHT NOW in a conversation turn — polled while the reply blocks. */
export interface TurnActivity {
  busy: boolean;
  since: number | null;
  tokens: { input: number; output: number; estimated: boolean };
  tools: TurnToolActivity[];
  preview: string | null;
}

/** Live ledger of a run in flight — what it has FILED so far, straight from the database. */
export interface RunProgress {
  run_id: number;
  status: string;
  totals: { proposals: number; questions: number };
  docs: Array<{ ref: string; proposals: number; questions: number }>;
  recent: Array<{ kind: string; text: string; at: string }>;
}

export interface RunDetail extends RunRow {
  engagement_code: string;
  transcript: TranscriptMessage[] | null;
  proposals: { id: number; kind: string; status: string; payload: Record<string, unknown>; created_at: string }[];
  documents_in_scope: { id: number; ref: string; original_filename: string; effective_date: string | null }[];
  live_log: string[];
}

export interface ScopeDocument {
  id: number;
  ref: string;
  original_filename: string;
  effective_date: string | null;
  source_type: string;
  status: string;
  text_length: number;
  no_text_layer: boolean;
  cited_by_requirements: number;
  seen_by_agent: boolean;
  covered: boolean;
}

// ───────────────────────── proposals ─────────────────────────

export type ProposalKind =
  | 'create_requirement'
  | 'update_requirement'
  | 'merge'
  | 'conflict'
  | 'create_deliverable'
  | 'create_deliverable_item'
  | 'create_ac'
  | 'create_test_case'
  | 'open_question';

export type ProposalStatus = 'pending' | 'accepted' | 'accepted_with_edits' | 'rejected' | 'superseded';

export const REJECT_REASONS = [
  { id: 'duplicate', label: 'Duplicate of an existing requirement' },
  { id: 'too_vague', label: 'Too vague / not testable' },
  { id: 'not_a_requirement', label: 'Not a requirement' },
  { id: 'wrong_evidence', label: 'Evidence does not support it' },
  { id: 'out_of_scope', label: 'Out of scope' },
  { id: 'other', label: 'Other' },
] as const;

export interface ProposalEvidence {
  document_id: number;
  document_ref: string;
  quoted_span: string;
  char_start?: number;
  char_end?: number;
  locator?: Record<string, unknown>;
  confidence?: number | null;
}

export interface ConflictPosition {
  statement: string;
  document_ref: string;
  quoted_span: string;
  effective_date?: string | null;
  speaker?: string | null;
}

export interface ProposalPayload {
  kind?: 'create' | 'update';
  statement?: string;
  rationale?: string | null;
  type?: string;
  priority?: string;
  category?: string | null;
  confidence?: number | null;
  target_ref?: string | null;
  previous_statement?: string | null;
  // conflict
  topic?: string;
  hinges_on?: string | null;
  position_a?: ConflictPosition;
  position_b?: ConflictPosition;
  // deliverable
  name?: string;
  description?: string | null;
  definition_of_done?: string | null;
  satisfies_refs?: string[];
  satisfies_requirement_ids?: number[];
  // acceptance criterion
  requirement_ref?: string;
  requirement_type?: string;
  measurement_method?: string | null;
  lint?: { pass: boolean; flags: { code: string; severity: string; message: string; term?: string }[] };
  // test case
  title?: string;
  preconditions?: string | null;
  expected_result?: string | null;
  test_data_ref?: string | null;
  steps?: { n: number; action: string; expected: string }[];
  ac_refs?: string[];
  acceptance_criterion_ids?: number[];
  // open question
  question?: string;
  context?: string | null;
  [key: string]: unknown;
}

export interface Proposal {
  id: number;
  engagement_id: number;
  run_id: number | null;
  kind: ProposalKind;
  target_requirement_id: number | null;
  payload: ProposalPayload;
  evidence: ProposalEvidence[];
  /** the ≤20-word line the model wrote for the queue rail — null until it has; never the record */
  descriptor: string | null;
  model_id: string | null;
  status: ProposalStatus;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  reviewer_note: string | null;
  reject_reason: string | null;
  created_at: string;
  run_mode: RunMode | null;
  run_status: RunStatus | null;
  run_stats: RunStats | null;
  run_created_at: string | null;
  /** The deliverable the run's workbench chat was ABOUT — a cross-deliverable proposal is one
   *  whose landing deliverable differs from this (shared/proposalSubject.ts). */
  run_context_ref: string | null;
  /** TRUE when the row sits in the review queue; FALSE when a workbench thread holds it
   *  (utils/proposalQueue.ts) — the page shows both, in separate tabs. */
  queued?: boolean;
  target_ref: string | null;
  target_statement: string | null;
  target_type: string | null;
  target_priority: string | null;
  target_category: string | null;
  target_status: string | null;
  source_document_refs: string[];
  evidence_count: number;
}

export interface RunHealth {
  id: number;
  mode: RunMode;
  status: RunStatus;
  stats: RunStats;
  model_id: string | null;
  created_at: string;
  finished_at: string | null;
  workspace_commit: string | null;
  proposal_count: number;
  pending_count: number;
  docs_in_scope_count: number;
  docs_covered_count: number;
}

export interface ProposalsResponse {
  engagement_id: number;
  count: number;
  proposals: Proposal[];
  runs: RunHealth[];
}

export interface ConversationResult {
  run: RunRow;
  reply: string;
  proposals: { id: number; kind: ProposalKind; status: string; payload: ProposalPayload }[];
  open_questions: { id: number; ref: string; question: string }[];
  turn_tool_calls: number;
}

// ───────────────────────── helpers ─────────────────────────

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

// ───────────────────────── the client ─────────────────────────

export const agentApi = {
  health: () => request<AgentHealth>('/agent/health'),

  workspace: (engagementId: number) => request<WorkspaceState>(`/agent/workspace${qs({ engagement_id: engagementId })}`),

  refreshWorkspace: (engagementId: number) =>
    request<{ dir: string; commitSha: string | null; state: WorkspaceState }>(
      '/agent/workspace/refresh',
      json({ engagement_id: engagementId }),
    ),

  documents: (engagementId: number) =>
    request<{ engagement_id: number; documents: ScopeDocument[] }>(`/agent/documents${qs({ engagement_id: engagementId })}`),

  listRuns: (engagementId: number, limit = 50) =>
    request<{ engagement_id: number; count: number; runs: RunRow[] }>(
      `/agent/runs${qs({ engagement_id: engagementId, limit })}`,
    ),

  getRun: (id: number) => request<RunDetail>(`/agent/runs/${id}`),

  runProgress: (id: number) => request<RunProgress>(`/agent/runs/${id}/progress`),

  /** The current turn's live doing — clock anchor, tokens, tool calls, streamed tail. */
  runActivity: (id: number) => request<TurnActivity>(`/agent/runs/${id}/activity`),

  /** Undo an accept: deletes the created artifact (deletion policy applies), proposal back to pending. */
  revertProposal: (id: number) =>
    request<{ reverted: boolean; deleted_ref: string | null; questions_removed: string[]; note: string }>(
      `/agent/proposals/${id}/revert`, { method: 'POST' },
    ),

  nudgeRun: (id: number, message: string) =>
    request<{ ok: boolean; note: string }>(`/agent/runs/${id}/nudge`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  startRun: (engagementId: number, body: { mode: RunMode; prompt?: string; document_ids?: number[] }) =>
    request<RunRow>('/agent/runs', json({ engagement_id: engagementId, ...body })),

  abortRun: (id: number) => request<RunRow>(`/agent/runs/${id}/abort`, json({})),

  /** What each proposal pass would cover right now (DESIGN §9). */
  passes: (engagementId: number) =>
    request<PassPlan>(`/agent/passes?engagement_id=${engagementId}`),

  /** Run one pass over the register. Returns immediately; watch the runs table. */
  runPass: (engagementId: number, pass: PassKind, onlyMissing = true, note?: string) =>
    request<RunRow & { pass: PassKind; label: string }>(
      '/agent/passes', json({ engagement_id: engagementId, pass, only_missing: onlyMissing, note: note || undefined }),
    ),

  /** Remove a finished run for good. Proposals it produced survive, with their run link cleared. */
  deleteRun: (id: number) =>
    request<{ deleted: number; proposals_kept: number; pending_kept: number }>(
      `/agent/runs/${id}`, { method: 'DELETE' },
    ),

  /** Clear finished runs that produced nothing — the accumulated throwaway chatter. */
  cleanupRuns: (engagementId: number, includeWithProposals = false) =>
    request<{ deleted: number; kept_because_they_have_proposals: number }>(
      '/agent/runs/cleanup', json({ engagement_id: engagementId, include_with_proposals: includeWithProposals }),
    ),

  conversation: (engagementId: number, message: string, runId?: number | null, contextRef?: string | null) =>
    request<ConversationResult>(
      '/agent/conversation',
      json({ engagement_id: engagementId, message, run_id: runId ?? null, context_ref: contextRef ?? null }),
    ),

  listProposals: (engagementId: number, filters: { status?: string; run_id?: number; kind?: string; scope?: 'queued' | 'all' } = {}) =>
    request<ProposalsResponse>(`/agent/proposals${qs({ engagement_id: engagementId, ...filters })}`),

  getProposal: (id: number) => request<Proposal>(`/agent/proposals/${id}`),

  /** Delete a PENDING machine proposal outright — no refusal record; the audit entry keeps the payload. */
  deleteProposal: (id: number) => request<{ deleted: boolean }>(`/agent/proposals/${id}`, { method: 'DELETE' }),

  /**
   * Accept. `discuss_with_client` accepts exactly as a plain accept AND files an operator
   * open question ("Refine REQ-012 with the client: …") on the client-call agenda — only
   * valid for create_requirement / update_requirement / create_ac proposals.
   */
  accept: (id: number, body: { edits?: Record<string, unknown>; note?: string; discuss_with_client?: boolean } = {}) =>
    request<{ proposal: Proposal; result: Record<string, unknown> }>(`/agent/proposals/${id}/accept`, json(body)),

  reject: (id: number, rejectReason: string, note?: string) =>
    request<Proposal>(`/agent/proposals/${id}/reject`, json({ reject_reason: rejectReason, note })),
};

export default agentApi;
